import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Card } from '../types/card';
import { AlertCircle, RefreshCcw, Swords, Shield, Zap, MapPin, Sword, GraduationCap, MessageCircle, Coffee, Star, Droplets, Wind, Flame, Filter } from 'lucide-react';
import { useAuth } from '../lib/auth';

type SortOrder = 'asc' | 'desc';

interface Filters {
  rarete: string[];
  univers: string[];
  scoreMin: number;
  scoreMax: number;
}

export default function CardCollection() {
  const [cards, setCards] = useState<Card[]>([]);
  const [filteredCards, setFilteredCards] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    rarete: [],
    univers: [],
    scoreMin: 0,
    scoreMax: 1000
  });
  const [sortBy, setSortBy] = useState<'score' | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const { user } = useAuth();

  const fetchCards = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      const isAdminUser = user.email === 'admin@baby-sittign.xyz';
      setIsAdmin(isAdminUser);

      let query = supabase
        .from('cards')
        .select('*')
        .order('created_at', { ascending: false });

      if (!isAdminUser) {
        query = query.eq('owner_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      setCards(data || []);
      setFilteredCards(data || []);
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError('Erreur lors du chargement des cartes');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    let result = [...cards];

    // Apply filters
    if (filters.rarete.length > 0) {
      result = result.filter(card => filters.rarete.includes(card.rarete));
    }
    if (filters.univers.length > 0) {
      result = result.filter(card => filters.univers.includes(card.univers));
    }

    result = result.filter(card => {
      const totalScore = calculateTotalScore(card);
      return totalScore >= filters.scoreMin && totalScore <= filters.scoreMax;
    });

    // Apply sorting
    if (sortBy === 'score') {
      result.sort((a, b) => {
        const scoreA = calculateTotalScore(a);
        const scoreB = calculateTotalScore(b);
        return sortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
      });
    }

    setFilteredCards(result);
  }, [cards, filters, sortBy, sortOrder]);

  const getStatColor = (univers: string) => {
    switch (univers) {
      case 'EAU':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500';
      case 'FEU':
        return 'bg-gradient-to-r from-red-500 to-orange-500';
      case 'AIR':
        return 'bg-gradient-to-r from-gray-400 to-slate-500';
      default:
        return 'bg-gradient-to-r from-indigo-500 to-purple-500';
    }
  };

  const getRarityStyle = (rarete: string) => {
    switch (rarete) {
      case 'COMMUN':
        return 'border-2 border-gray-300';
      case 'RARE':
        return 'border-8 border-blue-500 shadow-lg shadow-blue-500/50';
      case 'GOLD':
        return 'border-8 border-yellow-500 shadow-lg shadow-yellow-500/50';
      default:
        return 'border border-gray-200';
    }
  };

  const getUniversIcon = (univers: string) => {
    switch (univers) {
      case 'EAU':
        return <Droplets className="w-5 h-5 text-blue-500" />;
      case 'FEU':
        return <Flame className="w-5 h-5 text-red-500" />;
      case 'AIR':
        return <Wind className="w-5 h-5 text-gray-500" />;
      default:
        return null;
    }
  };

  const getRarityBadgeStyle = (rarete: string) => {
    switch (rarete) {
      case 'COMMUN':
        return 'bg-gray-100 text-gray-800';
      case 'RARE':
        return 'bg-blue-100 text-blue-800';
      case 'GOLD':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const ABSOLUTE_MAX_SCORE = 300;

  const StatBar = ({ value, icon: Icon, label, univers }: { value: number; icon: React.ElementType; label: string; univers: string }) => {
    const percentage = (value / ABSOLUTE_MAX_SCORE) * 100;
    
    return (
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-gray-600" />
        <div className="flex-1">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${getStatColor(univers)} transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
        <span className="text-sm font-medium text-gray-700 min-w-[3rem] text-right">
          {value}
        </span>
      </div>
    );
  };

  const calculateTotalScore = (card: Card) => {
    return card.attaque + card.defense + card.special;
  };

  const toggleFilter = (type: 'rarete' | 'univers', value: string) => {
    setFilters(prev => {
      const current = prev[type];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [type]: updated };
    });
  };

  const handleScoreRangeChange = (min: number, max: number) => {
    setFilters(prev => ({
      ...prev,
      scoreMin: min,
      scoreMax: max
    }));
  };

  const resetFilters = () => {
    setFilters({
      rarete: [],
      univers: [],
      scoreMin: 0,
      scoreMax: 1000
    });
    setSortBy(null);
    setSortOrder('desc');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <RefreshCcw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">Chargement des cartes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={() => fetchCards()}
            disabled={retrying}
            className="mt-3 text-sm text-red-600 hover:text-red-500 disabled:opacity-50"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Collection vide</h2>
          <p className="mt-2 text-gray-600">
            Vous n'avez pas encore de cartes. Rendez-vous dans la section Booster pour commencer votre collection !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Ma Collection</h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres {showFilters ? '▼' : '▲'}
          </button>
        </div>

        {showFilters && (
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Rareté */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Rareté</h3>
                <div className="space-y-2">
                  {['COMMUN', 'RARE', 'GOLD'].map((rarete) => (
                    <label key={rarete} className="inline-flex items-center mr-4">
                      <input
                        type="checkbox"
                        checked={filters.rarete.includes(rarete)}
                        onChange={() => toggleFilter('rarete', rarete)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">{rarete}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Univers */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Univers</h3>
                <div className="space-y-2">
                  {['EAU', 'AIR', 'FEU'].map((univers) => (
                    <label key={univers} className="inline-flex items-center mr-4">
                      <input
                        type="checkbox"
                        checked={filters.univers.includes(univers)}
                        onChange={() => toggleFilter('univers', univers)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">{univers}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Score */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Score Total</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={filters.scoreMin}
                      onChange={(e) => handleScoreRangeChange(Number(e.target.value), filters.scoreMax)}
                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      min="0"
                      max={filters.scoreMax}
                    />
                    <span>à</span>
                    <input
                      type="number"
                      value={filters.scoreMax}
                      onChange={(e) => handleScoreRangeChange(filters.scoreMin, Number(e.target.value))}
                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      min={filters.scoreMin}
                    />
                  </div>
                  <div>
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={sortBy === 'score'}
                        onChange={() => {
                          if (sortBy === 'score') {
                            setSortBy(null);
                          } else {
                            setSortBy('score');
                          }
                        }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">Trier par score</span>
                    </label>
                    {sortBy === 'score' && (
                      <button
                        onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                        className="ml-2 text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        {sortOrder === 'desc' ? '▼' : '▲'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center border-t pt-4">
              <button
                onClick={resetFilters}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Réinitialiser les filtres
              </button>
              <p className="text-sm text-gray-600">
                {filteredCards.length} carte{filteredCards.length !== 1 ? 's' : ''} trouvée{filteredCards.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCards.map((card) => (
          <div
            key={card.id}
            className={`bg-white rounded-lg shadow-md overflow-hidden ${getRarityStyle(card.rarete)} relative`}
          >
            <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRarityBadgeStyle(card.rarete)}`}>
                {card.rarete}
              </div>
              <div className="bg-white/90 p-1 rounded-full shadow-sm">
                {getUniversIcon(card.univers)}
              </div>
            </div>
            
            {card.image_url && (
              <div className="relative aspect-[4/3] bg-gray-100">
                <img
                  src={card.image_url}
                  alt={card.surnom}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{card.surnom}</h3>
                <div className="flex items-center gap-1 bg-gray-100 px-3 py-1.5 rounded-full">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="text-base font-semibold">{calculateTotalScore(card)}</span>
                </div>
              </div>

              <div className="space-y-4">
                <StatBar value={card.attaque} icon={Swords} label="Attaque" univers={card.univers} />
                <StatBar value={card.defense} icon={Shield} label="Défense" univers={card.univers} />
                <StatBar value={card.special} icon={Zap} label="Spécial" univers={card.univers} />
              </div>

              <div className="mt-4 space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{card.descriptions.zone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sword className="w-4 h-4" />
                  <span>{card.descriptions.attaque}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  <span>{card.descriptions.replique}</span>
                </div>
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  <span>{card.descriptions.diplome}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Coffee className="w-4 h-4" />
                  <span>{card.descriptions.anecdote}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}